import React, { useState, useEffect, useRef } from 'react';
import { Layout, Menu, Typography, Button, message } from 'antd';
import {
    DashboardOutlined,
    TeamOutlined,
    LogoutOutlined,
    MailOutlined,
    ApartmentOutlined,
    BarChartOutlined,
    TrophyOutlined,
    StarOutlined,
    BookOutlined,
    TagsOutlined,
    PieChartOutlined,
    BankOutlined,
    AppstoreOutlined,
    SolutionOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { hasPermission } from '../../utils/permissions';
import './LayoutMobile.css';

const { Header, Content, Sider } = Layout;
const { Title } = Typography;

const AdminLayout = ({ children, title }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
    const menuBarRef = useRef(null);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 992);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('permissions');
        message.success('Đăng xuất thành công!');
        navigate('/login');
    };

    const allMenuItems = [
        {
            key: 'dashboard',
            icon: <DashboardOutlined />,
            label: 'Tổng quan',
            permission: 'view_dashboard',
            onClick: () => navigate('/admin'),
        },
        {
            key: 'students',
            icon: <TeamOutlined />,
            label: 'Danh sách thí sinh',
            permission: 'view_student_list',
            onClick: () => navigate('/admin/student-list'),
        },
        {
            key: 'admission-scores',
            icon: <BarChartOutlined />,
            label: 'Điểm xét tuyển',
            permission: 'view_admission_scores',
            onClick: () => navigate('/admin/admission-scores'),
        },
        {
            key: 'students-ielts',
            icon: <StarOutlined />,
            label: 'Thí sinh IELTS',
            permission: 'view_ielts',
            onClick: () => navigate('/admin/students-ielts'),
        },
        {
            key: 'students-provincial-prize',
            icon: <TrophyOutlined />,
            label: 'Giải thưởng tỉnh',
            permission: 'view_prizes',
            onClick: () => navigate('/admin/students-provincial-prize'),
        },
        {
            key: 'subjects',
            icon: <BookOutlined />,
            label: 'Ngành học',
            permission: 'manage_subjects',
            onClick: () => navigate('/admin/subjects'),
        },
        {
            key: 'subject-types',
            icon: <TagsOutlined />,
            label: 'Nhóm ngành',
            permission: 'manage_subjects',
            onClick: () => navigate('/admin/subject-types'),
        },
        {
            key: 'subject-combinations',
            icon: <ApartmentOutlined />,
            label: 'Tổ hợp xét tuyển',
            permission: 'manage_subjects',
            onClick: () => navigate('/admin/subject-combinations'),
        },
        {
            key: 'admission-programs',
            icon: <AppstoreOutlined />,
            label: 'Ngành đánh giá cơ hội',
            permission: 'manage_benchmarks',
            onClick: () => navigate('/admin/admission-programs'),
        },
        {
            key: 'benchmarks',
            icon: <TrophyOutlined />,
            label: 'Quản lý điểm chuẩn',
            permission: 'manage_benchmarks',
            onClick: () => navigate('/admin/benchmarks'),
        },
        {
            key: 'email-logs',
            icon: <MailOutlined />,
            label: 'Email Logs',
            permission: 'view_email_logs',
            onClick: () => navigate('/admin/email-logs'),
        },
        {
            key: 'email-reminders',
            icon: <MailOutlined />,
            label: 'Gửi nhắc nhở',
            permission: 'send_reminders',
            onClick: () => navigate('/admin/email-reminders'),
        },
        {
            key: 'profile-stats',
            icon: <PieChartOutlined />,
            label: 'Thống kê hồ sơ',
            permission: 'view_statistics',
            onClick: () => navigate('/admin/profile-stats'),
        },
        {
            key: 'payment-logs',
            icon: <BankOutlined />,
            label: 'Thanh toán BIDV',
            permission: 'manage_payment_logs',
            onClick: () => navigate('/admin/payment-logs'),
        },
        {
            key: 'admission-results',
            icon: <SolutionOutlined />,
            label: 'Kết quả trúng tuyển',
            permission: 'manage_admission_results',
            onClick: () => navigate('/admin/admission-results'),
        },
    ];

    const menuItems = allMenuItems.filter(item => hasPermission(item.permission));

    const currentMenuKey =
        location.pathname === '/admin/student-list'              ? 'students' :
        location.pathname === '/admin/email-logs'                ? 'email-logs' :
        location.pathname === '/admin/subjects'                  ? 'subjects' :
        location.pathname === '/admin/subject-types'             ? 'subject-types' :
        location.pathname === '/admin/subject-combinations'      ? 'subject-combinations' :
        location.pathname === '/admin/admission-scores'          ? 'admission-scores' :
        location.pathname === '/admin/admission-programs'        ? 'admission-programs' :
        location.pathname === '/admin/benchmarks'                ? 'benchmarks' :
        location.pathname === '/admin/students-ielts'            ? 'students-ielts' :
        location.pathname === '/admin/students-provincial-prize' ? 'students-provincial-prize' :
        location.pathname === '/admin/email-reminders'          ? 'email-reminders' :
        location.pathname === '/admin/profile-stats'            ? 'profile-stats' :
        location.pathname === '/admin/payment-logs'             ? 'payment-logs' :
        location.pathname === '/admin/admission-results'        ? 'admission-results' :
        'dashboard';

    // Auto-scroll selected menu item into view
    useEffect(() => {
        if (isMobile && menuBarRef.current) {
            const selectedItem = menuBarRef.current.querySelector('.ant-menu-item-selected');
            if (selectedItem) {
                selectedItem.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center'
                });
            }
        }
    }, [currentMenuKey, isMobile]);

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Sider
                breakpoint="lg"
                collapsedWidth="0"
            >
                <div style={{ height: 32, margin: 16, background: 'rgba(255, 255, 255, 0.2)' }} />
                <Menu
                    theme="dark"
                    mode="inline"
                    selectedKeys={[currentMenuKey]}
                    items={menuItems}
                />
            </Sider>
            <Layout>
                <Header className="layout-header" style={{ padding: 0, background: '#fff' }}>
                    {/* Top bar: Title + Logout */}
                    <div className="header-top-bar">
                        <Title level={2} className="header-title" style={{ margin: 0 }}>
                            {title || 'Trang quản trị'}
                        </Title>
                        <div className="header-user-section">
                            <Button 
                                type="primary" 
                                icon={<LogoutOutlined />} 
                                onClick={handleLogout}
                                className="header-logout-btn"
                            >
                                Đăng xuất
                            </Button>
                        </div>
                    </div>

                    {/* Mobile horizontal menu */}
                    {isMobile && (
                        <div className="mobile-menu-bar" ref={menuBarRef}>
                            <Menu
                                mode="horizontal"
                                selectedKeys={[currentMenuKey]}
                                items={menuItems}
                            />
                        </div>
                    )}
                </Header>
                <Content style={{ margin: '24px 16px 0' }}>
                    <div style={{ padding: 24, minHeight: 360, background: '#fff' }}>
                        {children}
                    </div>
                </Content>
            </Layout>
        </Layout>
    );
};

export default AdminLayout; 